import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl';
import { parseISO, format, addDays, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import debounce from 'lodash/debounce';
import { useContrailContext } from './ContrailContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// Utility function for formatting dates in UTC
const formatDateUTC = (date) => {
    return formatInTimeZone(date, 'UTC', 'yyyy-MM-dd');
};

const GOESContrailLayer = ({ layerId, visible, currentDateTime, addCachedHour, cachedHours, isPlaying }) => {
    const { current: map } = useMap();
    const [contrails, setContrails] = useState([]);
    const context = useContrailContext();
    if (!context) {
        console.log("ContrailContext is undefined — check if the provider is wrapping this component.");
    }
    const { filteredContrails, setFilteredContrails } = useContrailContext();
    const [popupInfo, setPopupInfo] = useState(null);

    const dataCache = useRef({});
    const inProgressCache = useRef(new Set());

    useEffect(() => {
        const filterContrails = () => {
            const currentDateTimeUnix = currentDateTime.getTime();
            const filtered = contrails.map((contrail) => {
                const contrailTimeUnix = parseISO(contrail.timestamp).getTime();
                const timeDiffMinutes = (contrailTimeUnix - currentDateTimeUnix) / (1000 * 60);
                return {
                    ...contrail,
                    isHighlighted: Math.abs(timeDiffMinutes) < 0.5,
                    timeDiffMinutes,
                };
            }).filter((contrail) => contrail.timeDiffMinutes >= -5 && contrail.timeDiffMinutes < 5);
            setFilteredContrails(filtered);
        };

        if (contrails.length > 0) {
            filterContrails();
        } else {
            setFilteredContrails([]);
        }
    }, [contrails, currentDateTime, setFilteredContrails]);

    const isDataEmpty = (data) => {
        if (Array.isArray(data)) {
            return data.length === 0;
        }
        if (typeof data === 'object' && data !== null) {
            return Object.keys(data).length === 0;
        }
        return !data; // This will catch null, undefined, '', 0, false
    };

    const fetchHourData = useCallback(async (layerId, dateStr, hour) => {
        const cacheKey = `${layerId}_${dateStr}_${hour}`;
        if (dataCache.current[cacheKey] || inProgressCache.current.has(cacheKey)) {
            return dataCache.current[cacheKey] || null;
        }

        inProgressCache.current.add(cacheKey);
        try {
            const response = await fetch(`${API_BASE_URL}/contrails?date_str=${dateStr}&hour=${hour}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();

            if (!isDataEmpty(data)) {
                const firstTimestamp = parseISO(data[0].timestamp);
                const lastTimestamp = parseISO(data[data.length - 1].timestamp);
                console.log(`Debug: Fetched data for ${layerId}, ${dateStr}, hour ${hour}. Time range:`,
                    format(firstTimestamp, "yyyy-MM-dd HH:mm:ss 'UTC'", { timeZone: 'UTC' }),
                    'to',
                    format(lastTimestamp, "yyyy-MM-dd HH:mm:ss 'UTC'", { timeZone: 'UTC' })
                );

                // Only cache and add to cachedHour if data is not empty
                dataCache.current[cacheKey] = data;
                addCachedHour(layerId, parseISO(`${dateStr}T${hour.toString().padStart(2, '0')}:00:00Z`), hour);
                return data;
            } else {
                console.log(`Debug: No valid data fetched for ${dateStr}, hour ${hour}`);
                return null;
            }
        } catch (error) {
            console.error(`Debug: Error fetching data for ${cacheKey}:`, error);
            return null;
        } finally {
            inProgressCache.current.delete(cacheKey);
        }
    }, [addCachedHour]);

    const fetchRelevantData = useCallback(async () => {
        if (!visible) return;
        const currentHour = currentDateTime.getUTCHours();
        const currentDateStr = formatDateUTC(currentDateTime);
        const prevDateStr = formatDateUTC(subDays(currentDateTime, 1));
        const nextDateStr = formatDateUTC(addDays(currentDateTime, 1));

        const hoursToFetch = [
            { dateStr: currentDateStr, hour: currentHour },
            { dateStr: currentHour === 23 ? nextDateStr : currentDateStr, hour: (currentHour + 1) % 24 },
            { dateStr: currentHour === 0 ? prevDateStr : currentDateStr, hour: (currentHour - 1 + 24) % 24 }
        ];

        try {
            const fetchedData = await Promise.all(
                hoursToFetch.map(async ({ dateStr, hour }) => {
                    const data = await fetchHourData(layerId, dateStr, hour);
                    return data;
                })
            );

            const allContrails = fetchedData.flat().filter(Boolean);
            setContrails(allContrails);
        } catch (error) {
            console.error('Debug: Error in fetchRelevantData:', error);
        }
    }, [visible, currentDateTime, fetchHourData, layerId]);

    const debouncedFetchRelevantData = useMemo(
        () => debounce(fetchRelevantData, 300),
        [fetchRelevantData]
    );

    useEffect(() => {
        if (isPlaying) {
            // If playing, fetch immediately without debounce
            fetchRelevantData();
        } else {
            // If not playing (manual adjustment), use debounce
            debouncedFetchRelevantData();
        }

        return () => {
            debouncedFetchRelevantData.cancel();
        };
    }, [fetchRelevantData, debouncedFetchRelevantData, currentDateTime, isPlaying]);

    const contrailFeatures = useMemo(() => {
        return filteredContrails
            .filter(contrail => contrail.coordinates && contrail.coordinates.length >= 4)
            .map((contrail, index) => {
                return {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: contrail.coordinates.reduce((coords, _, i, arr) =>
                            i % 2 === 0 ? [...coords, [arr[i + 1], arr[i]]] : coords, []),
                    },
                    properties: {
                        isHighlighted: contrail.isHighlighted,
                        timestamp: contrail.timestamp,
                        hash: contrail.hash,
                        originalIndex: index,
                    },
                    id: `contrail-${index}`,
                };
            });
    }, [filteredContrails]);
    
    const handleMapClick = useCallback(
        (e) => {
            const clickedFeatures = e.features;
    
            if (clickedFeatures && clickedFeatures.length > 0) {
                const feature = clickedFeatures[0];
    
                // Check if the feature belongs to the contrail layer
                if (feature.layer.id.includes('contrail-layer')) {
                    const originalIndex = feature.properties.originalIndex;
                    const clickedContrail = filteredContrails[originalIndex];
                    
                    if (!clickedContrail) {
                        setPopupInfo(null);
                        return;
                    }
                    
                    // Extract the raw timestamp - we'll keep it in its original format
                    const rawTimestamp = clickedContrail.timestamp;
                    
                    // Extract the coordinates in the original format
                    const rawCoordinates = clickedContrail.coordinates;
                    
                    // Set popup info with raw data
                    setPopupInfo({
                        coordinates: [e.lngLat.lng, e.lngLat.lat],
                        rawTimestamp,
                        hash: clickedContrail.hash,
                        rawCoordinates
                    });
                } else {
                    setPopupInfo(null);
                }
            } else {
                setPopupInfo(null);
            }
        },
        [filteredContrails]
    );

    useEffect(() => {
        if (!map) return;

        // Attach the click event listener to the map
        map.on('click', `${layerId}-contrail-layer`, handleMapClick);

        return () => {
            if (map) {
                map.off('click', `${layerId}-contrail-layer`, handleMapClick);
            }
        };
    }, [map, layerId, handleMapClick]);

    // Check visibility before rendering
    if (!visible) {
        return null;
    }
    
    return (
        <>
            <Source 
                id={layerId + '-contrails'} 
                type="geojson" 
                data={{ type: 'FeatureCollection', features: contrailFeatures }} 
            >
                <Layer
                    id={layerId + '-contrail-layer'}
                    type="line"
                    paint={{
                        'line-color': [
                            'case',
                            ['get', 'isHighlighted'],  // If highlighted
                            'yellow',                  // Make it yellow
                            'white'                    // Default color
                        ],
                        'line-width': 4,
                        'line-opacity': 0.8
                    }}
                    interactive={true}
                />
            </Source>
            
            {popupInfo && (
                <Popup
                    longitude={popupInfo.coordinates[0]}
                    latitude={popupInfo.coordinates[1]}
                    closeButton={true}
                    closeOnClick={true}
                    onClose={() => setPopupInfo(null)}
                    anchor="top"
                    className="contrail-popup"
                >
                    <div className="contrail-popup-content">
                        <h4 className="text-lg font-medium mb-2">Contrail Details</h4>
                        <p><strong>Timestamp:</strong> {popupInfo.rawTimestamp}</p>
                        <div>
                            <strong>Hash: </strong>
                            <span className="font-mono bg-gray-100 px-2 py-1 rounded">{popupInfo.hash}</span>
                            <button 
                                className="ml-2 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 focus:ring-2 focus:ring-blue-300"
                                onClick={(event) => {
                                    const button = event.currentTarget;
                                    const originalText = button.textContent;
                                    
                                    navigator.clipboard.writeText(popupInfo.hash)
                                        .then(() => {
                                            // Change button text to indicate success
                                            button.textContent = "Copied!";
                                            button.classList.remove("bg-blue-500");
                                            button.classList.add("bg-green-500");
                                            
                                            // Reset button after 2 seconds
                                            setTimeout(() => {
                                                button.textContent = originalText;
                                                button.classList.remove("bg-green-500");
                                                button.classList.add("bg-blue-500");
                                            }, 2000);
                                        })
                                        .catch(err => {
                                            console.error('Failed to copy: ', err);
                                            
                                            // Show failure state
                                            button.textContent = "Failed";
                                            button.classList.remove("bg-blue-500");
                                            button.classList.add("bg-red-500");
                                            
                                            // Reset button after 2 seconds
                                            setTimeout(() => {
                                                button.textContent = originalText;
                                                button.classList.remove("bg-red-500");
                                                button.classList.add("bg-blue-500");
                                            }, 2000);
                                        });
                                }}
                            >
                                Copy
                            </button>
                        </div>
                        <div className="mt-2">
                            <strong>Coordinates:</strong>
                            <pre className="bg-gray-100 p-2 mt-1 rounded text-xs overflow-auto max-h-40">
                                {JSON.stringify(popupInfo.rawCoordinates, null, 2)}
                            </pre>
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
};

export default GOESContrailLayer;